import type { ReactNode } from "react";
import { cx } from "@/lib/cx";
import { labelSwatch } from "@/lib/format";

/**
 * A label or Field value as a compact chip: hairline border, 8px colour swatch, muted text. The
 * text always carries the meaning; the swatch only helps recognition. `field` prefixes the Field
 * name for screen readers ("Area: Auth").
 */
export function FieldChip({
  label,
  color,
  field,
  className,
}: {
  label: string;
  color?: string;
  field?: string;
  className?: string;
}) {
  return (
    <span
      title={field ? `${field}: ${label}` : label}
      className={cx(
        "inline-flex h-5 max-w-40 min-w-0 shrink-0 items-center gap-1.5 rounded-mark border border-line px-1.5 text-xs text-fg-muted",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full"
        style={{ backgroundColor: labelSwatch(color) }}
      />
      {field ? <span className="sr-only">{field}: </span> : null}
      <span className="truncate-title">{label}</span>
    </span>
  );
}

/** "+3" overflow marker after a capped chip list. */
export function MoreChip({ count, label }: { count: number; label: ReactNode }) {
  return (
    <span
      title={typeof label === "string" ? label : undefined}
      className="inline-flex h-5 shrink-0 items-center rounded-mark border border-line px-1.5 text-xs text-fg-subtle tnum"
    >
      +{count}
    </span>
  );
}
